/**
 * SupabaseUnreachableBanner
 * ------------------------------------------------------------------
 * Renders a single, dismissible warning banner when the Supabase
 * backend cannot be reached from the user's browser. Mounted once at
 * the Layout level — do NOT mount per-page (would duplicate probes).
 *
 * - Hidden when status is 'checking' or 'reachable'
 * - Distinguishes "browser offline" vs "backend unreachable"
 * - Uses semantic design tokens only (no hardcoded colors)
 */

import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSupabaseReachability } from '@/hooks/useSupabaseReachability';

export function SupabaseUnreachableBanner() {
  const { status, recheck, lastError } = useSupabaseReachability();

  if (status === 'checking' || status === 'reachable') return null;

  const isOffline = status === 'offline';
  const Icon = isOffline ? WifiOff : AlertTriangle;
  const title = isOffline
    ? 'You are offline'
    : 'Cannot reach the server';
  const message = isOffline
    ? 'Some features are unavailable until your connection returns.'
    : 'Login and data may be slow or fail. We will keep retrying automatically.';

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-destructive/10 border-b border-destructive/30 text-destructive-foreground"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Icon className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <span className="font-medium">{title}.</span>
          <span className="hidden sm:inline text-muted-foreground">{message}</span>
          {lastError && !isOffline && (
            <span className="hidden md:inline text-xs text-muted-foreground">
              ({lastError})
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={recheck}
          className="shrink-0 h-7 text-xs"
        >
          <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />
          Retry
        </Button>
      </div>
    </div>
  );
}
